import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'

/**
 * Shared wrapper style for the operations tab's grids (divisions sheet,
 * division teams sheet, roster). Kept in one place so the pending-row and
 * inline-edit treatments stay identical across grids.
 *
 * Deliberately NO row / header / hover / border repainting here.
 *
 * The house look — a SECONDARY_300 header over rows that zebra between
 * SECONDARY_300 and SECONDARY_400, hairline dividers, a brighter hover —
 * belongs to the shared `dataGridTheme`
 * (`src/components/ts-components/DataGrid/theme.ts`), and every grid in the
 * app has to agree on it. This file used to repaint all of it to a flatter
 * "roster table" palette (flat SECONDARY_400 rows under a darker
 * SECONDARY_500 header band, 10px muted header caps on a 34px strip), which
 * left Operations reading as a different product from the schedule sheet
 * beside it. Matching the Schedule tab means inheriting the theme rather
 * than overriding it, so only what is genuinely specific to THESE grids
 * stays below.
 */
/**
 * Header height shared by every operations grid, matching the Schedule tab's
 * `DetailsGrid`. Kept beside the style so the roster, teams and divisions
 * sheets cannot drift apart from each other or from Schedule.
 */
export const OPERATIONS_GRID_HEADER_HEIGHT = 44

/** Row height, likewise matching Schedule's `DetailsGrid`. */
export const OPERATIONS_GRID_ROW_HEIGHT = 48

/**
 * `cellClass` for a column whose cell is a pill (or a pill with a line under
 * it). Plain text centres on the cell's line-height; an inline pill rides the
 * text baseline and sits low, so these cells centre their content instead.
 */
export const OPERATIONS_PILL_CELL_CLASS = 'operations-pill-cell'

export const operationsGridStyle = css`
  width: 100%;
  min-width: 0;

  /* The grids pass rowHeight={OPERATIONS_GRID_ROW_HEIGHT}, which sizes the row
     box only. Cell content takes its line-height from the theme's own
     --ag-row-height (42px by default, so a 39px line), which left every cell's
     text ~3px above the middle of a 48px row. Matching the variable to the
     row centres it. Declared on .ag-root-wrapper because the theme sets the
     variable on an element between this wrapper and the cells. */
  .ag-root-wrapper {
    --ag-row-height: ${OPERATIONS_GRID_ROW_HEIGHT}px;
  }

  /* AG Grid's drag handle ships a wide right margin meant for a column of its
     own. Here it leads the team cell and reads as one unit with the seed
     beside it, so it sits closer. */
  .ag-drag-handle.ag-row-drag {
    margin-right: 4px;
  }

  /* The row-actions column is sized to its 28px trigger and nothing else, so
     the theme's usual cell padding would crowd the button. Centre it in the
     narrow column instead. */
  .operations-actions-cell {
    padding-left: 0;
    padding-right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .${OPERATIONS_PILL_CELL_CLASS} {
    display: flex;
    align-items: center;
  }

  /* A row with a mutation in flight recedes until the server answers. */
  .operations-row-pending .ag-cell {
    opacity: 0.55;
    transition: opacity 0.15s ease-in-out;
  }

  .ag-cell-inline-editing {
    border-color: ${colors.PRIMARY};
  }

  /* Narrow numeric columns (GAMES, PTS) are sized to their HEADER, not their
     one-to-three digits. The shared DataGrid theme spends 14px a side on
     header padding and numericColumn adds an inline sort arrow, which
     together ellipsised both labels to "G…" and "P…". Trimming the padding
     here buys the label its width back without stealing it from the record
     column, which is the only one that gets better with room. */
  .ag-header-cell.operations-tight-header {
    padding-left: 6px;
    padding-right: 6px;
  }
`
